/**
 * insightsEngine.js — Rê Leve (Painel de Marketing & Insights)
 *
 * Agrega clientes + pedidos simulados + kits para gerar visão executiva
 * em tempo real. Quando a API real estiver ativa, basta trocar
 * `getOrders()` por chamada ao backend — a estrutura dos insights não muda.
 *
 * Tudo determinístico baseado em seed estável (data + cliente.id) para
 * que o painel não dance entre refreshes.
 */

import { getAllCustomers, getAllKits } from './dataService';

// ── Hash determinístico simples (não-criptográfico) ────────────────────────
function seedHash(input) {
  let h = 2166136261;
  const s = String(input);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

function rand01(seed) {
  // Mulberry32 — PRNG estável a partir de seed inteiro
  let t = (seed + 0x6D2B79F5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return (((t ^ (t >>> 14)) >>> 0) / 4294967296);
}

function pick(arr, seed) {
  return arr[Math.floor(rand01(seed) * arr.length)];
}

// ── Gera histórico de pedidos simulado, mas plausível ──────────────────────
const TODAY = new Date();
TODAY.setHours(12, 0, 0, 0);

function daysAgo(n) {
  const d = new Date(TODAY);
  d.setDate(d.getDate() - n);
  return d;
}

function daysAhead(n) {
  const d = new Date(TODAY);
  d.setDate(d.getDate() + n);
  return d;
}

// Mapeia segmento → frequência semanal típica
const SEGMENT_FREQUENCY = {
  'cliente-fiel': 5,
  'cliente-alto-valor': 4,
  'cliente-recorrente': 3,
  'cliente-novo': 1,
  'cliente-em-atencao': 0.5,
  'cliente-inativo': 0,
  'cliente-possivel-coletivo': 2,
  'cliente-baixo-ticket': 1.5,
};

// Mapeia kit → ticket médio estimado (R$)
const KIT_PRICES = {
  'KIT_001': 24.90, // Dia Leve
  'KIT_002': 26.90, // Dia Controle
  'KIT_003': 32.90, // Dia Performance
  'KIT_004': 119.50, // Semana — combo
  'KIT_005': 28.90,
  'KIT_006': 20.90, // Equilíbrio
  'KIT_007': 14.90, // Sopa
  'KIT_008': 12.90, // Bolinho
};

const ADDON_PROBABILITY = {
  suco:     { name: 'Suco verde',    price: 6, p: 0.42 },
  bolinho:  { name: 'Bolinho banana', price: 5, p: 0.36 },
  sopa:     { name: 'Sopa abóbora',   price: 8, p: 0.18 },
  ovo:      { name: 'Ovo cozido',     price: 3, p: 0.22 },
};

let _ordersCache = null;

export function getOrders({ refresh = false } = {}) {
  if (_ordersCache && !refresh) return _ordersCache;

  const customers = getAllCustomers();
  const kits = getAllKits();
  const orders = [];

  customers.forEach((c) => {
    const seg = (c.segmentoComercial || c.segmento || c.segment || 'cliente-recorrente').toLowerCase();
    const freq = SEGMENT_FREQUENCY[seg] ?? 2;
    const seed = seedHash(c.id || c.clienteId || '0');

    // Histórico: últimas 4 semanas
    const totalPast = Math.round(freq * 4 * (0.75 + rand01(seed) * 0.5));
    for (let i = 0; i < totalPast; i++) {
      const daysBack = Math.floor(rand01(seed + i * 13) * 28);
      const kit = pick(kits.filter(k => (k.segmentosAlvo || []).includes(seg)).length
        ? kits.filter(k => (k.segmentosAlvo || []).includes(seg))
        : kits, seed + i * 7);
      const addons = [];
      Object.entries(ADDON_PROBABILITY).forEach(([id, cfg]) => {
        if (rand01(seed + i * 23 + id.charCodeAt(0)) < cfg.p) {
          addons.push({ id, name: cfg.name, price: cfg.price });
        }
      });
      const kitPrice = KIT_PRICES[kit.id] || 26.90;
      const addonsTotal = addons.reduce((s, a) => s + a.price, 0);
      orders.push({
        id: `ORD-${c.id}-${i}`,
        customerId: c.id || c.clienteId,
        customerName: c.nome || c.name || `Cliente ${c.id}`,
        segment: seg,
        kitId: kit.id,
        kitName: kit.nome,
        date: daysAgo(daysBack),
        dayOffset: -daysBack,
        kitPrice,
        addons,
        total: kitPrice + addonsTotal,
        source: 'simulated',
      });
    }

    // Pedidos confirmados futuros (assinaturas)
    if (freq >= 3) {
      const futureCount = Math.round(freq);
      for (let i = 0; i < futureCount; i++) {
        const daysAheadN = i + 1; // próximos N dias
        const kit = pick(kits.filter(k => (k.segmentosAlvo || []).includes(seg)).length
          ? kits.filter(k => (k.segmentosAlvo || []).includes(seg))
          : kits, seed + (i + 100) * 7);
        const kitPrice = KIT_PRICES[kit.id] || 26.90;
        orders.push({
          id: `ORD-FUT-${c.id}-${i}`,
          customerId: c.id || c.clienteId,
          customerName: c.nome || c.name || `Cliente ${c.id}`,
          segment: seg,
          kitId: kit.id,
          kitName: kit.nome,
          date: daysAhead(daysAheadN),
          dayOffset: daysAheadN,
          kitPrice,
          addons: [],
          total: kitPrice,
          source: 'subscription',
          status: 'confirmed',
        });
      }
    }
  });

  _ordersCache = orders;
  return orders;
}

// ── KPIs agregados ─────────────────────────────────────────────────────────
export function getKPIs() {
  const orders = getOrders();
  const last7 = orders.filter(o => o.dayOffset >= -7 && o.dayOffset <= 0);
  const last30 = orders.filter(o => o.dayOffset >= -30 && o.dayOffset <= 0);
  const prev7 = orders.filter(o => o.dayOffset >= -14 && o.dayOffset < -7);

  const revenue7 = last7.reduce((s, o) => s + o.total, 0);
  const revenuePrev7 = prev7.reduce((s, o) => s + o.total, 0);
  const revenue30 = last30.reduce((s, o) => s + o.total, 0);

  const customersServed = new Set(last30.map(o => o.customerId)).size;
  const ticketMedio = last7.length > 0 ? revenue7 / last7.length : 0;

  const totalCustomers = getAllCustomers().length;
  const atRisk = getAllCustomers().filter(c => {
    const seg = (c.segmentoComercial || c.segmento || '').toLowerCase();
    return seg === 'cliente-em-atencao' || seg === 'cliente-inativo';
  }).length;
  const churnRate = totalCustomers > 0 ? (atRisk / totalCustomers) * 100 : 0;

  const deltaRevenue = revenuePrev7 > 0 ? ((revenue7 - revenuePrev7) / revenuePrev7) * 100 : 0;

  return {
    revenue7,
    revenue30,
    revenuePrev7,
    deltaRevenue,
    pedidos7: last7.length,
    ticketMedio,
    customersServed,
    churnRate,
    atRiskCount: atRisk,
  };
}

// ── Vendas por dia da semana (últimos 28 dias) ─────────────────────────────
export function getWeekdayBreakdown() {
  const orders = getOrders().filter(o => o.dayOffset >= -28 && o.dayOffset <= 0);
  const dias = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
  const data = dias.map(d => ({ dia: d, pedidos: 0, receita: 0 }));
  orders.forEach(o => {
    const idx = o.date.getDay();
    data[idx].pedidos += 1;
    data[idx].receita += o.total;
  });
  return data;
}

// ── Top kits ───────────────────────────────────────────────────────────────
export function getTopKits({ days = 7, limit = 5 } = {}) {
  const orders = getOrders().filter(o => o.dayOffset >= -days && o.dayOffset <= 0);
  const counts = {};
  orders.forEach(o => {
    if (!counts[o.kitId]) {
      counts[o.kitId] = { kitId: o.kitId, name: o.kitName, pedidos: 0, receita: 0 };
    }
    counts[o.kitId].pedidos += 1;
    counts[o.kitId].receita += o.total;
  });
  return Object.values(counts)
    .sort((a, b) => b.pedidos - a.pedidos)
    .slice(0, limit);
}

// ── Segmentação ────────────────────────────────────────────────────────────
export function getSegmentBreakdown() {
  const customers = getAllCustomers();
  const orders = getOrders().filter(o => o.dayOffset >= -30 && o.dayOffset <= 0);
  const map = {};

  customers.forEach(c => {
    const seg = (c.segmentoComercial || c.segmento || 'cliente-novo').toLowerCase();
    if (!map[seg]) map[seg] = { segment: seg, customers: 0, orders: 0, revenue: 0 };
    map[seg].customers += 1;
  });
  orders.forEach(o => {
    const seg = o.segment;
    if (!map[seg]) map[seg] = { segment: seg, customers: 0, orders: 0, revenue: 0 };
    map[seg].orders += 1;
    map[seg].revenue += o.total;
  });

  return Object.values(map).sort((a, b) => b.revenue - a.revenue);
}

// ── Alertas de churn (clientes em risco) ───────────────────────────────────
export function getChurnAlerts({ limit = 8 } = {}) {
  const customers = getAllCustomers();
  return customers
    .filter(c => {
      const seg = (c.segmentoComercial || c.segmento || '').toLowerCase();
      return seg === 'cliente-em-atencao' || seg === 'cliente-inativo' || seg === 'cliente-baixo-ticket';
    })
    .slice(0, limit)
    .map(c => {
      const seg = (c.segmentoComercial || c.segmento || '').toLowerCase();
      const orders = getOrders().filter(o => o.customerId === (c.id || c.clienteId));
      const lastOrder = orders.sort((a, b) => b.date - a.date)[0];
      const daysSinceLast = lastOrder ? Math.floor((TODAY - lastOrder.date) / 86400000) : 999;

      let actionLine;
      if (seg === 'cliente-inativo') {
        actionLine = 'Mandar Léve com cupom de reativação';
      } else if (seg === 'cliente-em-atencao') {
        actionLine = 'Léve avisa: "vi que tu sumiu, bora retomar?"';
      } else {
        actionLine = 'Oferecer upgrade pra Kit Dia Performance';
      }

      return {
        customerId: c.id || c.clienteId,
        name: c.nome || c.name || c.id,
        segment: seg,
        daysSinceLast,
        lastTotal: lastOrder?.total || 0,
        action: actionLine,
        priority: seg === 'cliente-inativo' ? 'alta' : seg === 'cliente-em-atencao' ? 'média' : 'baixa',
      };
    })
    .sort((a, b) => {
      const order = { alta: 0, média: 1, baixa: 2 };
      return order[a.priority] - order[b.priority];
    });
}

// ── Oportunidades de upsell/cross-sell ─────────────────────────────────────
export function getOpportunities({ limit = 6 } = {}) {
  const orders = getOrders().filter(o => o.dayOffset >= -30 && o.dayOffset <= 0);

  // Clientes que pedem Dia Leve mas não Dia Performance → upsell candidates
  const ups = {};
  const cross = {};
  orders.forEach(o => {
    if (o.kitId === 'KIT_001' || o.kitId === 'KIT_006') {
      // pedem leve/equilíbrio → candidato upsell
      if (!ups[o.customerId]) {
        ups[o.customerId] = { customerId: o.customerId, name: o.customerName, pedidos: 0, ticket: 0 };
      }
      ups[o.customerId].pedidos += 1;
      ups[o.customerId].ticket += o.total;
    }
    // Clientes sem addons → candidato cross-sell
    if ((!o.addons || o.addons.length === 0) && o.kitId !== 'KIT_007' && o.kitId !== 'KIT_008') {
      if (!cross[o.customerId]) {
        cross[o.customerId] = { customerId: o.customerId, name: o.customerName, pedidos: 0 };
      }
      cross[o.customerId].pedidos += 1;
    }
  });

  const upsellOps = Object.values(ups)
    .filter(u => u.pedidos >= 3)
    .sort((a, b) => b.pedidos - a.pedidos)
    .slice(0, Math.ceil(limit / 2))
    .map(u => ({
      type: 'upsell',
      customerId: u.customerId,
      name: u.name,
      reason: `${u.pedidos} pedidos de Kit Dia Leve em 30 dias`,
      action: 'Oferecer Kit Dia Performance (+R$8/marmita)',
      potentialMonthly: u.pedidos * 8,
    }));

  const crossOps = Object.values(cross)
    .filter(c => c.pedidos >= 2)
    .sort((a, b) => b.pedidos - a.pedidos)
    .slice(0, Math.floor(limit / 2))
    .map(c => ({
      type: 'cross-sell',
      customerId: c.customerId,
      name: c.name,
      reason: `${c.pedidos} pedidos sem adicionais em 30 dias`,
      action: 'Léve oferece suco/bolinho no próximo pedido',
      potentialMonthly: c.pedidos * 5.5, // média entre suco e bolinho
    }));

  return [...upsellOps, ...crossOps].slice(0, limit);
}

// ── Sugestões de campanha pela Léve ────────────────────────────────────────
export function getCampaignSuggestions() {
  const kpis = getKPIs();
  const top = getTopKits({ days: 14, limit: 3 });
  const churnAlerts = getChurnAlerts({ limit: 50 });
  const suggestions = [];

  // Sugestão 1: Topo da semana
  if (top.length > 0) {
    suggestions.push({
      id: 'sug-top',
      title: `Massa! ${top[0].name} bombou essa semana`,
      body: `Foram ${top[0].pedidos} pedidos. Vale criar push pra clientes que ainda não experimentaram.`,
      action: 'Criar campanha "experimente o queridinho"',
      icon: '🔥',
      level: 'alta',
    });
  }

  // Sugestão 2: Reativação se tiver churn alto
  if (kpis.atRiskCount >= 3) {
    suggestions.push({
      id: 'sug-churn',
      title: `Vixe, ${kpis.atRiskCount} clientes sumiram`,
      body: `${churnAlerts.filter(c => c.priority === 'alta').length} estão inativos. Cupom -20% na 1ª recompra acende eles.`,
      action: 'Disparar Léve com cupom RETORNA20',
      icon: '⚠️',
      level: 'alta',
    });
  }

  // Sugestão 3: Crescimento ou queda
  if (kpis.deltaRevenue < -5) {
    suggestions.push({
      id: 'sug-delta',
      title: `Receita caiu ${Math.abs(kpis.deltaRevenue).toFixed(0)}% vs semana anterior`,
      body: 'Vale reforçar story de Instagram com Combo Premium + ativar bolsa térmica.',
      action: 'Ativar campanha "Combo Premium"',
      icon: '📉',
      level: 'alta',
    });
  } else if (kpis.deltaRevenue > 10) {
    suggestions.push({
      id: 'sug-delta-up',
      title: `Tá danado! Receita +${kpis.deltaRevenue.toFixed(0)}% vs semana anterior`,
      body: 'Manter ritmo: agendar post de gratidão + reforçar entrega na sexta.',
      action: 'Agendar post de "obrigada Recife"',
      icon: '📈',
      level: 'média',
    });
  }

  // Sugestão 4: Programa de indicação
  suggestions.push({
    id: 'sug-indicar',
    title: 'Indicação tá parada essa semana',
    body: 'Manda Léve pra todos os clientes-fiéis com convite de indicação + bônus.',
    action: 'Disparar campanha "Indica um amigo"',
    icon: '🤝',
    level: 'média',
  });

  return suggestions;
}
